import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useApiService } from '../services/apiService';
import { authService } from '../services/authService';
import { getItem, setItem, removeItem, STORAGE_KEYS } from '../utils/storage';
import { clearIdentity } from '../utils/analytics';

// Define types for our user data structure based on the API response
export interface ClientAssignment {
  client_id: number;
  role: 'OWNER' | 'IC';
  client: {
    agency_name: string;
    url: string;
    active: boolean;
  };
}

export interface Client {
  id: number;
  agency_name: string;
  url: string;
  active: boolean;
}

export interface User {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_admin: boolean;
  is_tools_user?: boolean;
  client_assignments: ClientAssignment[];
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  clients: Client[];
  setClients: (clients: Client[]) => void;
  selectedClientId: number | null;
  setSelectedClientId: (id: number | null) => void;
  isRoutingInProgress: boolean;
  setIsRoutingInProgress: (value: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  handleViewModeChange: (newIsAdmin: boolean, bypass_cache?: boolean) => Promise<void>;
  refreshClients: (bypass_cache?: boolean) => Promise<void>;
  isAdmin: () => boolean;
  isToolsUser: () => boolean;
  isICRole: () => boolean;
  isGroupAdmin: () => boolean;
  isCognitoAdmin: () => boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  // Client data
  clientData: Client | null;
  viewMode: 'admin' | 'user';
  invalidateClientsCache: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  // Initialize selectedClientId from localStorage if available
  const [selectedClientId, setSelectedClientId] = useState<number | null>(() => {
    const stored = getItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
    if (stored) {
      const clientId = parseInt(stored, 10);
      return !isNaN(clientId) ? clientId : null;
    }
    return null;
  });
  const [clientData, setClientData] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [isRoutingInProgress, setIsRoutingInProgress] = useState(false);
  const [viewMode, setViewMode] = useState<'admin' | 'user'>('user');
  const api = useApiService();
  // Track if user has been loaded at least once (to distinguish between initial load and logout)
  const hasLoadedUserRef = useRef<boolean>(false);

  // Cookie auth: the source of truth is the mp_csrf cookie (JS-readable proxy
  // for the httpOnly mp_access / mp_refresh pair). We consider the user
  // authenticated when /api/auth/session resolved to a real user. The
  // apiService interceptor handles silent /refresh on 401 — there is no need
  // for client-side TTL bookkeeping, focus polling, or localStorage-based
  // session checks. Token lifetime is entirely server-managed.
  const isAuthenticated = useMemo(() => {
    return authService.isAuthenticated() && user !== null;
  }, [user]);

  // Fetch user data on mount and when token changes
  useEffect(() => {
    const fetchUser = async () => {
      if (!authService.isAuthenticated()) {
        console.log('[UserContext] No token, clearing user state');
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log('[UserContext] Fetching user session from API');

        const response = await api.get<{ user: User; is_admin: boolean }>('/api/auth/session');

        if (response?.data?.user) {
          console.log('[UserContext] User session loaded successfully:', response.data.user);
          setUser(response.data.user);
          hasLoadedUserRef.current = true;

          // Always fetch clients when user is loaded (don't rely on stale state)
          try {
            console.log('[UserContext] Fetching clients...');
            const clientResponse = await api.get<Client[]>('/api/clients');
            const allClients = clientResponse.data || [];
            const filteredClients = response.data.user.is_admin
              ? allClients
              : allClients.filter(client =>
                  response.data.user.client_assignments?.some(assignment => assignment.client_id === client.id)
                );
            setClients(filteredClients);
            console.log('[UserContext] Clients loaded successfully:', filteredClients.length, 'clients');
            setIsLoading(false); // Set loading to false only after clients are loaded
          } catch (clientError: any) {
            console.error('[UserContext] Failed to fetch clients:', clientError);
            // Don't set empty array on error - keep existing clients if any
            // This prevents clearing clients on temporary network errors
            // Only set empty if this is the first load and we have no clients
            if (clients.length === 0) {
              setClients([]);
            }
            setIsLoading(false); // Still set loading to false even on error
            // Don't fail the entire user fetch if clients fail
          }
        } else {
          console.log('[UserContext] No user data in response, clearing user state');
          setUser(null);
          setClients([]);
          hasLoadedUserRef.current = false;
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error('[UserContext] Failed to initialize user:', error);

        // Check if this is a token expiration error
        if (error?.response?.status === 401) {
          console.log('[UserContext] Token expired, clearing user state');
          setUser(null);
          setClients([]);
          hasLoadedUserRef.current = false;
          authService.clearToken();
          setIsLoading(false);
          // Don't redirect immediately - let the user see the login page naturally
        } else {
          // For other errors, don't clear user/clients - might be a temporary network issue
          // Only clear if we haven't loaded a user yet
          if (!hasLoadedUserRef.current) {
            setUser(null);
            setClients([]);
          }
          setIsLoading(false);
        }
      }
    };

    fetchUser();
  }, [api]);

  // Set up session expiration timeout when user is logged in
  // (Session-expiration timers and focus/visibility polling were removed when
  // the app switched to httpOnly-cookie auth — token lifetime is now managed
  // entirely server-side and the axios interceptor calls /api/auth/refresh
  // on 401 to mint a new access cookie. The old timer fired on every focus
  // because loginTime is never written by the cookie flow.)

  // Validate and restore selectedClientId from localStorage when clients are loaded
  // This effect MUST run before the persistence effect to ensure restoration happens first
  useEffect(() => {
    console.log('[UserContext] Validation effect running. user:', !!user, 'clients.length:', clients.length);

    if (!user || clients.length === 0) {
      console.log('[UserContext] Validation effect: Skipping - user or clients not ready');
      return;
    }

    // Always check localStorage first - it's the source of truth
    const storedClientIdStr = getItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
    const currentSelectedId = selectedClientId;

    console.log('[UserContext] Validation effect: storedClientIdStr:', storedClientIdStr, 'currentSelectedId:', currentSelectedId);
    console.log('[UserContext] Available client IDs:', clients.map(c => c.id));

    // If we have a stored client ID, validate and restore it
    if (storedClientIdStr) {
      const storedClientId = parseInt(storedClientIdStr, 10);
      if (!isNaN(storedClientId)) {
        // Validate the stored client ID exists in the current clients array
        const clientExists = clients.some(c => c.id === storedClientId);
        console.log('[UserContext] Validation effect: storedClientId', storedClientId, 'exists in clients:', clientExists);

        if (clientExists) {
          // Restore it if state doesn't match localStorage
          if (currentSelectedId !== storedClientId) {
            console.log('[UserContext] Restoring selectedClientId from localStorage:', storedClientId, '(current:', currentSelectedId, ')');
            setSelectedClientId(storedClientId);
          } else {
            console.log('[UserContext] selectedClientId already matches localStorage:', storedClientId);
          }
          // Ensure it's persisted
          setItem(STORAGE_KEYS.SELECTED_CLIENT_ID, storedClientId.toString());
          return;
        } else {
          // Stored client ID is no longer valid, clear it
          console.log('[UserContext] Stored client ID is invalid, clearing:', storedClientId);
          removeItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
          if (currentSelectedId === storedClientId) {
            setSelectedClientId(null);
          }
          return;
        }
      } else {
        console.log('[UserContext] Validation effect: storedClientIdStr is not a valid number:', storedClientIdStr);
      }
    } else {
      console.log('[UserContext] Validation effect: No stored client ID in localStorage');
    }

    // If no stored value but we have a current selection, validate it
    if (currentSelectedId !== null) {
      const clientExists = clients.some(c => c.id === currentSelectedId);
      if (clientExists) {
        // Current selection is valid, persist it
        console.log('[UserContext] Validation effect: Current selection is valid, persisting:', currentSelectedId);
        setItem(STORAGE_KEYS.SELECTED_CLIENT_ID, currentSelectedId.toString());
      } else {
        // Current selection is invalid, clear it
        console.log('[UserContext] Validation effect: Current selection is invalid, clearing:', currentSelectedId);
        setSelectedClientId(null);
      }
    } else {
      console.log('[UserContext] Validation effect: No current selection and no stored value');
    }
  }, [user, clients]); // Removed selectedClientId from dependencies to avoid loops

  // Handle client selection and persist to localStorage
  // This runs AFTER validation to persist any changes
  useEffect(() => {
    // Only clear localStorage if user was previously loaded and is now null (actual logout)
    // Don't clear during initial load when user is null but hasn't been loaded yet
    if (!user) {
      // If user has been loaded before and is now null, this is a logout - clear everything
      if (hasLoadedUserRef.current) {
        console.log('[UserContext] Persistence effect: User logged out, clearing selectedClientId');
        setSelectedClientId(null);
        setClientData(null);
        removeItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
      }
      return;
    }

    // Mark that user has been loaded
    if (!hasLoadedUserRef.current) {
      hasLoadedUserRef.current = true;
    }

    // Don't run until clients are loaded (validation effect handles restoration)
    if (clients.length === 0) {
      return;
    }

    // Persist selectedClientId to localStorage whenever it changes
    if (selectedClientId !== null) {
      // Validate the client exists before persisting
      const clientExists = clients.some(c => c.id === selectedClientId);
      if (clientExists) {
        // Persist to localStorage
        const stored = getItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
        if (stored !== selectedClientId.toString()) {
          console.log('[UserContext] Persisting selectedClientId to localStorage:', selectedClientId);
          setItem(STORAGE_KEYS.SELECTED_CLIENT_ID, selectedClientId.toString());
        }
        // Update client data when selection changes
        const selectedClient = clients.find(c => c.id === selectedClientId);
        setClientData(selectedClient || null);
      } else {
        // Invalid client ID, clear it
        console.log('[UserContext] Invalid client ID, clearing:', selectedClientId);
        setSelectedClientId(null);
        removeItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
        setClientData(null);
      }
    } else {
      // Only clear localStorage if there's actually no selection AND we're sure clients are loaded
      // Don't clear if validation might still restore from localStorage
      const stored = getItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
      if (stored) {
        // There's a stored value but state is null - let validation handle it
        // Don't clear localStorage here as validation effect will handle restoration
        setClientData(null);
      } else {
        // No stored value and no selection - clear client data
        setClientData(null);
      }
    }
  }, [user, clients, selectedClientId]);

  // Handle view mode changes
  const handleViewModeChange = async (newIsAdmin: boolean, bypass_cache: boolean = false) => {
    if (user) {
      setIsRoutingInProgress(true);

      try {
        setClients([]);

        const response = await api.get<Client[]>('/api/clients', {
          params: { bypass_cache }
        });
        const allClients = response.data;

        const filteredClients = newIsAdmin
          ? allClients
          : allClients.filter(client =>
              user.client_assignments?.some(assignment => assignment.client_id === client.id)
            );

        setClients(filteredClients);
        setViewMode(newIsAdmin ? 'admin' : 'user');
        setUser({
          ...user,
          is_admin: newIsAdmin
        });
      } catch (error) {
        console.error('Error fetching clients:', error);
        setError('Failed to fetch clients');
      } finally {
        setIsRoutingInProgress(false);
      }
    }
  };

  // Refresh clients
  const refreshClients = async (bypass_cache: boolean = false) => {
    try {
      const response = await api.get<Client[]>('/api/clients', {
        params: { bypass_cache }
      });
      const allClients = response.data;

      const filteredClients = user?.is_admin
        ? allClients
        : allClients.filter(client =>
            user?.client_assignments?.some(assignment => assignment.client_id === client.id)
          );

      setClients(filteredClients);
    } catch (error) {
      console.error('Error refreshing clients:', error);
      setError('Failed to refresh clients');
    }
  };

  // Invalidate clients cache
  const invalidateClientsCache = async () => {
    await refreshClients(true);
  };

  // Reload user data + clients. Called by Login.tsx on successful sign-in.
  // Must fetch /api/clients too — the initial fetchUser useEffect won't re-fire
  // after a fresh login (its dep array doesn't include auth state), so without
  // this the user has user set but clients=[], lands on /welcome.
  const reloadUser = async () => {
    try {
      const response = await api.get<{ user: User; is_admin: boolean }>('/api/auth/session');
      if (!response?.data?.user) return;
      setUser(response.data.user);
      hasLoadedUserRef.current = true;

      try {
        const clientResponse = await api.get<Client[]>('/api/clients');
        const allClients = clientResponse.data || [];
        const filteredClients = response.data.user.is_admin
          ? allClients
          : allClients.filter(client =>
              response.data.user.client_assignments?.some(a => a.client_id === client.id)
            );
        setClients(filteredClients);
      } catch (clientError) {
        console.error('[UserContext] Failed to reload clients:', clientError);
      }
    } catch (error) {
      console.error('Error reloading user:', error);
    }
  };

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.is_admin || false;
  }, [user]);

  // Tools access: admins implicitly have it; plus anyone flagged as tools-only.
  const isToolsUser = useCallback(() => {
    return !!(user?.is_admin || user?.is_tools_user);
  }, [user]);

  // v1 callsites destructure these — stubbed for cookie-auth User model
  // which doesn't carry the Cognito-era role split. Sidebar treats anyone
  // without is_admin as a regular user, which matches the historical IC
  // meaning closely enough.
  const isICRole = useCallback(() => !(user?.is_admin), [user]);
  const isGroupAdmin = useCallback(() => false, []);
  const isCognitoAdmin = useCallback(() => !!user?.is_admin, [user]);

  // Logout
  const logout = useCallback(async () => {
    await authService.logout();
    clearIdentity();
    hasLoadedUserRef.current = false; // Reset the flag on logout
    setUser(null);
    setClients([]);
    setSelectedClientId(null);
    setClientData(null);
    removeItem(STORAGE_KEYS.SELECTED_CLIENT_ID);
    window.location.href = '/';
  }, []);

  const value = useMemo(() => ({
    user,
    setUser,
    clients,
    setClients,
    selectedClientId,
    setSelectedClientId,
    isRoutingInProgress,
    setIsRoutingInProgress,
    error,
    setError,
    handleViewModeChange,
    refreshClients,
    isAdmin,
    isToolsUser,
    isICRole,
    isGroupAdmin,
    isCognitoAdmin,
    isLoading,
    logout,
    isAuthenticated,
    clientData,
    viewMode,
    invalidateClientsCache,
    reloadUser
  }), [
    user,
    clients,
    selectedClientId,
    isRoutingInProgress,
    error,
    isAdmin,
    isToolsUser,
    isLoading,
    isAuthenticated,
    clientData,
    viewMode
  ]);

  // Don't block rendering while checking authentication - let pages handle their own loading states
  // Public pages should render immediately, protected pages can check isLoading themselves
  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
